import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { transactionAPI } from '../services/api';
import { Search, Filter, MapPin, Home, Grid, CheckCircle, Clock, AlertCircle, Eye, Edit } from 'lucide-react';
import { formatDate, formatCurrency } from '../utils/formatters';

const Cemetery = () => {
    const navigate = useNavigate();
    const [filters, setFilters] = useState({
        page: 1,
        limit: 10,
        search: '',
        status: '',
        // 묘소가 배정된 계약만 조회
        has_mausoleum: true
    });
    const [searchInput, setSearchInput] = useState('');
    const [statusInput, setStatusInput] = useState('');

    const { data, isLoading, error } = useQuery({
        queryKey: ['cemetery-contracts', filters],
        queryFn: () => transactionAPI.getTransactions(filters),
        select: (response) => ({
            ...response.data.pagination,
            data: response.data.transactions,
        }),
        keepPreviousData: true,
    });

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1
        }));
    };

    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }));
    };

    const handleQuery = () => {
        setFilters(prev => ({
            ...prev,
            search: searchInput,
            status: statusInput,
            page: 1
        }));
    };

    const handleClearFilters = () => {
        setSearchInput('');
        setStatusInput('');
        setFilters(prev => ({
            ...prev,
            search: '',
            status: '',
            page: 1
        }));
    };

    useEffect(() => {
        setSearchInput(filters.search);
        setStatusInput(filters.status);
    }, []);

    if (isLoading) {
        return <div className="loading">묘소 정보를 불러오는 중...</div>;
    }

    if (error) {
        return (
            <div className="error">
                <h2 className="text-lg font-semibold text-red-800 mb-2">묘소 정보를 불러오는 중 오류가 발생했습니다</h2>
                <p className="text-red-600 mb-2">{error.message}</p>
                {error.response && (
                    <div className="text-sm text-red-500">
                        <p>상태 코드: {error.response.status}</p>
                        <p>응답 데이터: {JSON.stringify(error.response.data)}</p>
                    </div>
                )}
                <button 
                    onClick={() => window.location.reload()} 
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    다시 시도
                </button>
            </div>
        );
    }

    const getStatusColor = (status) => {
        switch (status) {
            case '완료': return 'status-completed';
            case '진행중': return 'status-progress';
            case '서류 대기': return 'status-waiting';
            default: return 'status-cancelled';
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">묘소 관리</h1>
                <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-500">
                        배정된 묘소: {data?.total || 0}개
                    </div>
                </div>
            </div>

            {/* 검색 및 필터 */}
            <div className="card mb-6">
                <div className="grid grid-4" style={{ gap: '16px', alignItems: 'end' }}>
                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1">
                            검색
                        </label>
                        <div className="relative">
                            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="묘적번호, 사망자명 검색"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="input w-full pl-10"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1">
                            계약 상태
                        </label>
                        <select
                            value={statusInput}
                            onChange={(e) => setStatusInput(e.target.value)}
                            className="select w-full"
                        >
                            <option value="">전체</option>
                            <option value="진행중">진행중</option>
                            <option value="완료">완료</option>
                            <option value="서류 대기">서류 대기</option>
                            <option value="취소">취소</option>
                        </select>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1">
                            페이지당 항목 수
                        </label>
                        <select
                            value={filters.limit}
                            onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                            className="select w-full"
                        >
                            <option value={10}>10개</option>
                            <option value={20}>20개</option>
                            <option value={50}>50개</option>
                        </select>
                    </div>

                    <div className="flex gap-2">
                        <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
                            초기화
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleQuery}>조회</button>
                    </div>
                </div>
            </div>

            {/* 묘소 목록 테이블 */}
            {data?.data?.length > 0 ? (
                <div className="card">
                    <div className="overflow-x-auto">
                        <table className="table w-full">
                            <thead>
                                <tr>
                                    <th>묘적번호</th>
                                    <th>사망자명</th>
                                    <th>단지</th>
                                    <th>묘역</th>
                                    <th>열</th>
                                    <th>번호</th>
                                    <th>크기(평형)</th>
                                    <th>계약일</th>
                                    <th>계약 상태</th>
                                    <th>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.data.map(contract => (
                                    <tr key={contract.id}>
                                        <td className="font-medium">
                                            {contract.mausoleum?.member_number || '정보 없음'}
                                        </td>
                                        <td>
                                            {contract.deads?.[0]?.name || '정보 없음'}
                                        </td>
                                        <td>
                                            {contract.mausoleum?.complex || '-'}
                                        </td>
                                        <td>
                                            {contract.mausoleum?.area || '-'}
                                        </td>
                                        <td>
                                            {contract.mausoleum?.row_number || '-'}
                                        </td>
                                        <td>
                                            {contract.mausoleum?.number || '-'}
                                        </td>
                                        <td>
                                            {contract.mausoleum?.size || '-'}
                                        </td>
                                        <td>
                                            {formatDate(contract.contract_date)}
                                        </td>
                                        <td>
                                            <span className={`status-badge ${getStatusColor(contract.status)}`}>
                                                {contract.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="flex gap-2">
                                                <button 
                                                    onClick={() => navigate(`/contracts/${contract.id}`)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                    title="상세 보기"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                <button 
                                                    onClick={() => navigate(`/contracts/edit/${contract.id}`)}
                                                    className="text-green-600 hover:text-green-900"
                                                    title="수정"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* 페이지네이션 */}
                    {data?.totalPages > 1 && (
                        <div className="flex justify-between items-center mt-6">
                            <div className="text-sm text-gray-700">
                                총 {data.total}개 중 {data.from}-{data.to}개
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handlePageChange(data.currentPage - 1)}
                                    disabled={data.currentPage <= 1}
                                    className="btn btn-secondary"
                                >
                                    이전
                                </button>
                                <span className="px-3 py-2 text-sm text-gray-700">
                                    {data.currentPage} / {data.totalPages}
                                </span>
                                <button
                                    onClick={() => handlePageChange(data.currentPage + 1)}
                                    disabled={data.currentPage >= data.totalPages}
                                    className="btn btn-secondary"
                                >
                                    다음
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="empty-state">
                    <MapPin size={48} className="text-gray-300 mb-4" />
                    <p className="text-gray-500">배정된 묘소가 없습니다.</p>
                </div>
            )}
        </div>
    );
};

export default Cemetery;